'use client'
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useTranslations } from 'next-intl'
import { usePathname } from '@/src/i18n/navigation'

import { useAppSelector, useAppDispatch } from '@/src/redux/store'
import { selectAuth, refreshAuthUser, clearAuthUser } from '@/src/redux/slices/authSlice'
import { setCurrency } from '@/src/redux/slices/currencySlice'
import { navLinks, languageOptions, currencyOptions } from '@/src/constants/header'
import { useGetCurrencyDataQuery } from '@/src/api/rtkQuery/currencyApi'
import { useSignInFlowVariant } from '@/src/hooks/useSignInFlowVariant'
import { deleteCookie } from '@/src/utils/browser'
import { getActiveVertical } from '@/src/utils/navigation/getActiveVertical'
import { getLanguageByCode } from '@/src/utils/language'
import { mapApiCurrencyOptions } from '@/src/utils/currency'
import { setLanguageCookie } from '@/src/utils/language'
import FlightIcon from '@mui/icons-material/Flight'
import HotelIcon from '@mui/icons-material/Hotel'

import { CTHeader } from './CTHeader'
import { SignInModalWrapper } from '../flyinHeader/SignInModal'

const CTHeaderContainer: React.FC = () => {
    const [isMounted, setIsMounted] = useState(false)
    const [modals, setModals] = useState<{ signIn: boolean }>({ signIn: false })
    const userMenuAnchorRef = useRef<HTMLElement | null>(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const [selected, setSelected] = useState({
        language: languageOptions[0],
        currency: currencyOptions[0]
    })

    // Fetch currency list from API
    const { data: currencyData, isLoading: isCurrencyLoading, isError: isCurrencyError } = useGetCurrencyDataQuery()

    // Map API data to expected shape for dropdown (using utility)
    const apiCurrencyOptions = React.useMemo(() => mapApiCurrencyOptions(currencyData), [currencyData])

    const currency = useAppSelector(state => state.currency.selected)
    const dispatch = useAppDispatch()

    const t = useTranslations('header')
    const tCurrency = useTranslations('Currency')
    const user = useAppSelector(selectAuth)
    const pathname = usePathname()

    // Determine active vertical based on current pathname
    const activeVertical = useMemo(() => {
        if (!isMounted) return null
        return getActiveVertical()
    }, [isMounted, pathname])

    // Define which routes should show the language/currency dropdown
    const showLangCurrDropdown = React.useMemo(() => {
        if (pathname === '/' || /^\/[a-z]{2}$/.test(pathname)) return true
        if (pathname === '/flights/results' || /^\/[a-z]{2}\/flights\/results/.test(pathname)) return true
        return false
    }, [pathname])

    // Check if current page is offers page - disable sign in on offers page
    const isOffersPage = pathname.includes('/offers')

    // Check if we're on itinerary page and OTP flow is active from AB
    const isItineraryPage = pathname.includes('/flights/itinerary')
    const { variant: signInFlowVariant } = useSignInFlowVariant()
    const isOtpFlow = signInFlowVariant === 'otp'
    const shouldHideSignInOnItinerary = isItineraryPage && isOtpFlow

    useEffect(() => {
        // Only run on client-side
        if (typeof window === 'undefined') return
        dispatch(refreshAuthUser())
        setIsMounted(true)
    }, [dispatch])

    useEffect(() => {
        if (!isMounted) return

        // Update currency if needed
        if (!isCurrencyLoading && !isCurrencyError && apiCurrencyOptions.length > 0) {
            const found = apiCurrencyOptions.find((opt: any) => opt.code === currency)
            if (found) {
                setSelected(prev => ({
                    ...prev,
                    currency: found
                }))
            }
        }

        // Read persisted language (using utility)
        const persistedLang = localStorage.getItem('selectedLanguage')
        if (persistedLang) {
            const found = getLanguageByCode(persistedLang)
            setSelected(prev => ({
                ...prev,
                language: found
            }))
        }
    }, [isMounted, currency, apiCurrencyOptions, isCurrencyLoading, isCurrencyError])

    const options = {
        language: languageOptions,
        currency:
            !isCurrencyLoading && !isCurrencyError && apiCurrencyOptions.length > 0
                ? apiCurrencyOptions
                : currencyOptions // fallback to static
    }

    const onMenuOpen = useCallback((menu: 'user', event: React.MouseEvent<HTMLElement>) => {
        userMenuAnchorRef.current = event.currentTarget
        setMenuOpen(true)
    }, [])

    const onMenuClose = useCallback((menu: 'user') => {
        userMenuAnchorRef.current = null
        setMenuOpen(false)
    }, [])

    const onSelect = useCallback(
        (type: 'language' | 'currency', value: any) => {
            setSelected(prev => ({ ...prev, [type]: value }))
            if (type === 'currency') {
                dispatch(setCurrency({ code: value.code, symbol: value.symbol }))
            } else if (type === 'language') {
                setLanguageCookie(value.code)
                localStorage.setItem('selectedLanguage', value.code)
                // Always set path to /ar/ or /en/ at the root
                const base = window.location.href
                const newPath = value.code === 'ar' ? base.replace('/en', '/ar') : base.replace('/ar', '/en')
                window.location.href = newPath
            }
            onMenuClose('user')
        },
        [dispatch, onMenuClose]
    )

    const onModalOpen = useCallback((modal: 'signIn') => {
        setModals(prev => ({ ...prev, [modal]: true }))
    }, [])

    const onModalClose = useCallback((modal: 'signIn') => {
        setModals(prev => ({ ...prev, [modal]: false }))
    }, [])

    // Sign out handler
    const handleSignOut = useCallback(() => {
        dispatch(clearAuthUser())
        const domain = '.' + window.location.hostname
        deleteCookie('usermisc', domain)
        deleteCookie('userid', domain)
        deleteCookie('userData', domain)
        dispatch(refreshAuthUser())
        onMenuClose('user')
    }, [dispatch, onMenuClose])

    // User menu items
    const userMenuItems = useMemo(
        () => [
            { label: t('userMenu.myBookings'), href: '/accounts/trips' },
            { label: t('userMenu.myWallet'), href: '/accounts/wallet' },
            { label: t('userMenu.myReviews'), href: '/manageReviews' },
            { label: t('userMenu.giveFeedback'), href: '/feedback' }
        ],
        [t]
    )

    // Icon mapping for nav items
    const getNavIcon = useCallback((label: string) => {
        const iconMap: { [key: string]: React.ReactNode } = {
            flights: <FlightIcon sx={{ fontSize: 20, color: 'inherit' }} />,
            hotels: <HotelIcon sx={{ fontSize: 20, color: 'inherit' }} />
        }
        return iconMap[label.toLowerCase()] || null
    }, [])

    // Create nav links for Cleartrip with active state
    const ctNavLinks = useMemo(() => {
        return [
            { label: 'Flights', href: '/', isActive: activeVertical === 'flights' },
            { label: 'Hotels', href: '/hotels', isActive: activeVertical === 'hotels' }
        ]
    }, [activeVertical])

    const displayUser = isMounted ? user : { signedIn: false }

    return (
        <>
            <CTHeader
                navLinks={ctNavLinks}
                options={options}
                selected={selected}
                menuAnchors={{ user: menuOpen ? userMenuAnchorRef.current : null }}
                onMenuOpen={onMenuOpen}
                onMenuClose={onMenuClose}
                onSelect={onSelect}
                onModalOpen={onModalOpen}
                t={t}
                tCurrency={tCurrency}
                user={displayUser}
                userMenuItems={userMenuItems}
                onSignOut={handleSignOut}
                showLangCurrDropdown={showLangCurrDropdown}
                getNavIcon={getNavIcon}
                hideSignIn={isOffersPage || shouldHideSignInOnItinerary}
            />
            <SignInModalWrapper
                open={modals.signIn}
                onClose={() => onModalClose('signIn')}
                onMenuClose={() => onMenuClose('user')}
            />
        </>
    )
}

export { CTHeaderContainer }
export default CTHeaderContainer
